import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RemoteRoute } from './components/RemoteHost';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { CustomFieldsPage } from './pages/CustomFieldsPage';
import { DataQualityPage } from './pages/DataQualityPage';
import { OrgPage } from './pages/OrgPage';
import { PlatformAdminPage } from './pages/PlatformAdminPage';
import { ReportBuilderPage } from './pages/ReportBuilderPage';
import { AccountsPage } from './pages/AccountsPage';
import { AgentsPage } from './pages/AgentsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CommissionsPage } from './pages/CommissionsPage';
import { CommissionTiersAdminPage } from './pages/CommissionTiersAdminPage';
import { MyCommissionPayoutsPage } from './pages/MyCommissionPayoutsPage';
import { HandoffInboxPage } from './pages/HandoffInboxPage';
import { PlaybooksAdminPage } from './pages/PlaybooksAdminPage';
import { RevenueCadenceAdminPage } from './pages/RevenueCadenceAdminPage';
import { RevenueMeetingPage } from './pages/RevenueMeetingPage';
import { SdrWorkspacePage } from './pages/SdrWorkspacePage';
import { AmWorkspacePage } from './pages/AmWorkspacePage';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentTemplatesPage } from './pages/DocumentTemplatesPage';
import { InfluenceMapPage } from './pages/InfluenceMapPage';
import { CompetitorsPage } from './pages/CompetitorsPage';
import { QuotasPage } from './pages/QuotasPage';
import { InboxPage } from './pages/InboxPage';
import { DeletionRequestQueuePage } from './pages/DeletionRequestQueuePage';
import { SuggestionPromptsAdminPage } from './pages/SuggestionPromptsAdminPage';
import { AttachmentStorageAdminPage } from './pages/AttachmentStorageAdminPage';
import { MyProfilePage } from './pages/MyProfilePage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import { LeadsPage } from './pages/LeadsPage';
import { LoginPage } from './pages/LoginPage';
import { TesseraCallbackPage } from './pages/TesseraCallbackPage';
// Epic 128 — external client portal (white-labeled, read-only). Mounted as a
// first-class route group in the host shell, with its own session + guard.
import { PortalProtectedRoute } from './components/portal/PortalProtectedRoute';
import { PortalLayout } from './pages/portal/PortalLayout';
import { PortalLoginPage } from './pages/portal/PortalLoginPage';
import { PortalDashboardPage } from './pages/portal/PortalDashboardPage';
// 131.34 — public Drive share viewer (unauthenticated, by token).
import { PublicDriveFolderPage } from './pages/PublicDriveFolderPage';
import { PublicDriveFilePage } from './pages/PublicDriveFilePage';
import { OpportunitiesPage } from './pages/OpportunitiesPage';
import { OpportunityDetailPage } from './pages/OpportunityDetailPage';
import { ProcessTemplatesPage } from './pages/ProcessTemplatesPage';
import { ProposalsPage } from './pages/ProposalsPage';
import { AIAgentsForIndustriesPage } from './pages/AIAgentsForIndustriesPage';
import { ServicesCatalogPage } from './pages/ServicesCatalogPage';
import { ProductsCatalogPage } from './pages/ProductsCatalogPage';
import { SearchPage } from './pages/SearchPage';
import { TasksInboxPage } from './pages/TasksInboxPage';
import { ProductsPage } from './pages/ProductsPage';
import { BrandingPage } from './pages/BrandingPage';
import { SegmentationPage } from './pages/SegmentationPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { TenantSettingsPage } from './pages/TenantSettingsPage';
import { CampaignAnalyticsPage } from './pages/CampaignAnalyticsPage';
import { ChainsPage } from './pages/ChainsPage';
import { ChainEditorPage } from './pages/ChainEditorPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { MyEmailSettingsPage } from './pages/MyEmailSettingsPage';
import { CustomersPage } from './pages/CustomersPage';
import { UsersPage } from './pages/UsersPage';
import { AdminModuleStorefrontPage } from './pages/AdminModuleStorefrontPage';
import { ModuleStorefrontPage } from './pages/ModuleStorefrontPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { CampaignDetailPage } from './pages/CampaignDetailPage';
import { ChannelAnalyticsPage } from './pages/ChannelAnalyticsPage';
import { AttributionPage } from './pages/AttributionPage';
import { MktgSalesAlignPage } from './pages/MktgSalesAlignPage';
import { LeadQualificationPage } from './pages/LeadQualificationPage';
import { ImportDetailPage } from './pages/ImportDetailPage';
import { ImportsPage } from './pages/ImportsPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { LeadCaptureFormsPage } from './pages/LeadCaptureFormsPage';
import { CampaignLandingPagesPage } from './pages/CampaignLandingPagesPage';
import { PublicCampaignLandingPage } from './pages/PublicCampaignLandingPage';
import { LeadCaptureFormEditPage } from './pages/LeadCaptureFormEditPage';
import { QualificationBanksPage } from './pages/QualificationBanksPage';
import { QualificationBankEditPage } from './pages/QualificationBankEditPage';
import { QualificationConversationsPage } from './pages/QualificationConversationsPage';
import { QualificationConversationDetailPage } from './pages/QualificationConversationDetailPage';
import { DncListPage } from './pages/DncListPage';
import { WorkflowRunsPage } from './pages/WorkflowRunsPage';
import { WorkflowAnalyticsPage } from './pages/WorkflowAnalyticsPage';
import { WorkflowMarketplacePage } from './pages/WorkflowMarketplacePage';
import { ConnectorIntegrationsPage } from './pages/ConnectorIntegrationsPage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { PublicBookingPage } from './pages/PublicBookingPage';
import { BillingPage } from './pages/BillingPage';
import { FunnelExplorerPage } from './pages/FunnelExplorerPage';
import { CockpitRepPage } from './pages/CockpitRepPage';
import { CockpitManagerPage } from './pages/CockpitManagerPage';
import { CockpitBoardPage } from './pages/CockpitBoardPage';
import { VoiceCredentialsPage } from './pages/VoiceCredentialsPage';
import { ProcessMapPage } from './pages/ProcessMapPage';
import { ApiBuilderPage } from './pages/ApiBuilderPage';
import { WebhooksPage } from './pages/WebhooksPage';
import { TargetAccountsPage } from './pages/TargetAccountsPage';
import { PublicLeadFormPage } from './pages/PublicLeadFormPage';
import { PublicSurveyPage } from './pages/PublicSurveyPage';
import { ESignPage } from './pages/ESignPage';
import { CalendarPage } from './pages/CalendarPage';
import { DrivePage } from './pages/DrivePage';
import { PublicSignPage } from './pages/PublicSignPage';
import { StyleGuidePage } from './pages/StyleGuidePage';
import { EnrichmentSettingsPage } from './pages/EnrichmentSettingsPage';
import { ExternalProvidersAdminPage } from './pages/ExternalProvidersAdminPage';
import { ChannelLinesPage } from './pages/ChannelLinesPage';
import { ConversationRoutingPage } from './pages/ConversationRoutingPage';
import { DialerPage } from './pages/DialerPage';
import { DialerAnalyticsPage } from './pages/DialerAnalyticsPage';
import { CoachingCallsPage } from './pages/CoachingCallsPage';
import { SendTimeAnalyticsPage } from './pages/SendTimeAnalyticsPage';
import { ConnectorMarketplacePage } from './pages/ConnectorMarketplacePage';
import { VerticalSettingsPage } from './pages/VerticalSettingsPage';
import { TenantHierarchyPage } from './pages/TenantHierarchyPage';
import { RegulatoryPage } from './pages/RegulatoryPage';
import { ConversationIntelligencePage } from './pages/ConversationIntelligencePage';
import { FormTemplatesPage } from './pages/FormTemplatesPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { AgencyPortfolioPage } from './pages/AgencyPortfolioPage';
import { BrandingProvider } from './contexts/BrandingContext';
import { VoiceScriptsPage } from './pages/VoiceScriptsPage';
import { VoiceRoutesPage } from './pages/VoiceRoutesPage';
import { VoiceSessionMonitorPage } from './pages/VoiceSessionMonitorPage';
import { VoiceLibraryPage } from './pages/VoiceLibraryPage';
import { PowerDialerAdminPage } from './pages/PowerDialerAdminPage';
import { VoiceSessionPage } from './pages/VoiceSessionPage';
import { EmailPoolsPage } from './pages/EmailPoolsPage';
import { EmailDomainSetupPage } from './pages/EmailDomainSetupPage';
import { SequencesPage } from './pages/SequencesPage';
import { SequenceEditorPage } from './pages/SequenceEditorPage';
import { SequenceAnalyticsPage } from './pages/SequenceAnalyticsPage';
import { TextAgentsPage } from './pages/TextAgentsPage';
import { ConversationsPage } from './pages/ConversationsPage';
import { ChatWidgetPage } from './pages/ChatWidgetPage';
import { useAuthStore } from './stores/authStore';
import { DemoListPage } from './pages/demos/DemoListPage';
import { DemoStudioPage } from './pages/demos/DemoStudioPage';
import { DemoEditorPage } from './pages/demos/DemoEditorPage';
import { DemoAnalyticsPage } from './pages/demos/DemoAnalyticsPage';
import { DemoBrandDefaultsPage } from './pages/admin/DemoBrandDefaultsPage';
import { DemoCatalogAdminPage } from './pages/admin/DemoCatalogAdminPage';
import { LivefeedPage } from './pages/LivefeedPage';
import { MessagesPage } from './pages/MessagesPage';
import { WorkgroupsPage } from './pages/WorkgroupsPage';
import { TimeClockPage } from './pages/TimeClockPage';
import { MyDevicesPage } from './pages/MyDevicesPage';
import { NotificationsAdminPage } from './pages/NotificationsAdminPage';
import { SurveysPage } from './pages/SurveysPage';
import { UserAgreementsPage } from './pages/UserAgreementsPage';
import { ExtranetPage } from './pages/ExtranetPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { WarehousePage } from './pages/WarehousePage';
import { SitesPage } from './pages/SitesPage';
import { IncomingEmailPage } from './pages/IncomingEmailPage';
import { AgreementsListPage } from './pages/agreements/AgreementsListPage';
import { AgreementDetailPage } from './pages/agreements/AgreementDetailPage';
import { AgreementTemplatesPage } from './pages/agreements/AgreementTemplatesPage';
import { AgreementTemplateEditPage } from './pages/agreements/AgreementTemplateEditPage';
import { SalesTeamPage } from './pages/SalesTeamPage';
// Epic 131.45 — Quotes/Proposals (native Go rebuild).
import { QuotesPage } from './pages/quotes/QuotesPage';
import { QuoteEditorPage } from './pages/quotes/QuoteEditorPage';
import { OAuthClientsPage } from './pages/admin/OAuthClientsPage';
import { MarketplacePage } from './pages/admin/MarketplacePage';
import { EventSubscriptionsPage } from './pages/admin/EventSubscriptionsPage';
import { InboundWebhooksPage } from './pages/admin/InboundWebhooksPage';
import { OAuthConsentPage } from './pages/OAuthConsentPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SignupRedirectPage } from './pages/SignupRedirectPage';

// Marketing landing — large static markup, lazy-loaded so it stays out of
// the authenticated main chunk.
const PublicLandingPage = lazy(() =>
  import('./pages/PublicLandingPage').then((m) => ({ default: m.PublicLandingPage })),
);

function DefaultRedirect() {
  const role = useAuthStore((s) => s.user?.role);
  // Story 46.4 — elevated roles land on the portfolio (campaign-centric);
  // sales reps + pre-sales stay on the action inbox (their flat aggregated
  // attention surface).
  let target = '/dashboard';
  if (role === 'SALES_REPRESENTATIVE' || role === 'PRE_SALES') {
    target = '/inbox';
  } else if (
    role === 'TENANT_ADMIN' ||
    role === 'PLATFORM_ADMIN' ||
    role === 'SALES_MANAGER'
  ) {
    target = '/portfolio';
  } else if (role === 'AGENCY_OWNER') {
    // Story 83.3 — Agency owners land on their cross-tenant portfolio.
    target = '/agency/portfolio';
  }
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <BrandingProvider>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Auth recovery + public marketing surface (commit d5a11d0). All
         standalone (no AppShell chrome), outside ProtectedRoute. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/signup" element={<SignupRedirectPage />} />
      <Route
        path="/welcome"
        element={
          <Suspense fallback={null}>
            <PublicLandingPage />
          </Suspense>
        }
      />
      {/* Tessera OIDC callback — receives ?code=&state= from Tessera's
         /oauth2/authorize, exchanges the code for tokens, sets session. */}
      <Route path="/auth/callback" element={<TesseraCallbackPage />} />
      {/* Story 48.4 — public lead capture landing page. No auth, no
         sidebar. Tenant branding applied based on the form's tenant. */}
      <Route path="/forms/:token" element={<PublicLeadFormPage />} />
      {/* Story 75.2 — public booking page. No auth required. */}
      <Route path="/book/:tenantSlug/:userSlug" element={<PublicBookingPage />} />
      {/* Story 109.1 — Kairos-hosted campaign landing page. No auth. */}
      <Route
        path="/l/:tenantSlug/:pageSlug"
        element={<PublicCampaignLandingPage />}
      />
      {/* Epic 226 "utilizable" — public survey response page. No auth. */}
      <Route path="/s/:token" element={<PublicSurveyPage />} />
      {/* Epic 226 "utilizable" — public e-signature page. No auth. */}
      <Route path="/sign/:token" element={<PublicSignPage />} />
      {/* 131.34 — public Drive share viewer (folder/file by token). No auth. */}
      <Route path="/p/drive/folder/:token" element={<PublicDriveFolderPage />} />
      <Route path="/p/drive/file/:token" element={<PublicDriveFilePage />} />
      {/* Epic 200 — OAuth 2.0 consent/approval screen. Authenticated but
         standalone (no AppShell chrome); ProtectedRoute bounces anonymous
         visitors to /login?returnTo=… */}
      <Route
        path="/oauth/consent"
        element={
          <ProtectedRoute>
            <OAuthConsentPage />
          </ProtectedRoute>
        }
      />
      {/* Epic 128 — external client portal. Public login + a guarded,
          white-labeled shell with its OWN session (usePortalAuthStore), fully
          separate from the staff AppShell. Read-only client surface. */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route
        path="/portal"
        element={
          <PortalProtectedRoute>
            <PortalLayout />
          </PortalProtectedRoute>
        }
      >
        <Route index element={<PortalDashboardPage />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DefaultRedirect />} />
        {/* Federated plugin remotes (123.5): /r/<capabilityKey> mounts the
            remote registered for that capability; the gate is in the host. */}
        <Route path="/r/:capabilityKey" element={<RemoteRoute />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/inbox/deletion-requests" element={<DeletionRequestQueuePage />} />
        <Route path="/admin/suggestion-prompts" element={<SuggestionPromptsAdminPage />} />
        <Route path="/admin/attachment-storage" element={<AttachmentStorageAdminPage />} />
        <Route path="/me/profile" element={<MyProfilePage />} />
        {/* Epic 115 — UserMenu role switcher exposes "Edit profile" at
            /account/profile. Kept as a route alias to /me/profile so the
            existing page (session preferences) is the canonical surface;
            future personal-account screens layer onto the same path. */}
        <Route path="/account/profile" element={<MyProfilePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/:id" element={<AccountDetailPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="/process-templates" element={<ProcessTemplatesPage />} />
        <Route path="/opportunities" element={<OpportunitiesPage />} />
        <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
        <Route path="/document-templates" element={<DocumentTemplatesPage />} />
        <Route path="/opportunities/:id/influence" element={<InfluenceMapPage />} />
        <Route path="/competitors" element={<CompetitorsPage />} />
        <Route path="/quotas" element={<QuotasPage />} />
        <Route path="/opportunities/:id/proposals" element={<ProposalsPage />} />
        <Route path="/tasks" element={<TasksInboxPage />} />
        <Route path="/feed" element={<LivefeedPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/workgroups" element={<WorkgroupsPage />} />
        <Route path="/time-clock" element={<TimeClockPage />} />
        <Route path="/account/devices" element={<MyDevicesPage />} />
        <Route path="/admin/notifications" element={<NotificationsAdminPage />} />
        <Route path="/surveys" element={<SurveysPage />} />
        <Route path="/policies" element={<UserAgreementsPage />} />
        <Route path="/admin/extranet" element={<ExtranetPage />} />
        <Route path="/admin/payments" element={<PaymentsPage />} />
        <Route path="/esign" element={<ESignPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/drive" element={<DrivePage />} />
        <Route path="/admin/warehouse" element={<WarehousePage />} />
        <Route path="/admin/sites" element={<SitesPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/admin/audit" element={<AuditLogPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/admin/agents" element={<AgentsPage />} />
        <Route path="/admin/platform" element={<PlatformAdminPage />} />
        <Route path="/admin/custom-fields" element={<CustomFieldsPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/admin/products/ai-agents" element={<AIAgentsForIndustriesPage />} />
        <Route path="/admin/catalog/services" element={<ServicesCatalogPage />} />
        <Route path="/admin/catalog/products" element={<ProductsCatalogPage />} />
        <Route path="/admin/branding" element={<BrandingPage />} />
        <Route path="/admin/segmentation" element={<SegmentationPage />} />
        <Route path="/admin/commissions" element={<CommissionsPage />} />
        <Route path="/admin/commission-tiers" element={<CommissionTiersAdminPage />} />
        <Route path="/account/commission-payouts" element={<MyCommissionPayoutsPage />} />
        <Route path="/handoff-inbox" element={<HandoffInboxPage />} />
        <Route path="/admin/playbooks" element={<PlaybooksAdminPage />} />
        <Route path="/admin/revenue-cadence" element={<RevenueCadenceAdminPage />} />
        <Route path="/revenue-meeting" element={<RevenueMeetingPage />} />
        <Route path="/cockpit/sdr" element={<SdrWorkspacePage />} />
        <Route path="/cockpit/am" element={<AmWorkspacePage />} />
        <Route path="/analytics/data-quality" element={<DataQualityPage />} />
        <Route path="/analytics/reports" element={<ReportBuilderPage />} />
        <Route path="/admin/org" element={<OrgPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/admin/settings" element={<TenantSettingsPage />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/platform-modules" element={<AdminModuleStorefrontPage />} />
        <Route path="/admin/modules" element={<ModuleStorefrontPage />} />
        <Route path="/admin/permissions" element={<PermissionsPage />} />
        <Route path="/analytics/campaigns" element={<CampaignAnalyticsPage />} />
        <Route path="/admin/chains" element={<ChainsPage />} />
        <Route path="/admin/chains/:id/edit" element={<ChainEditorPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/admin/process-map" element={<ProcessMapPage />} />
        <Route path="/account/change-password" element={<ChangePasswordPage />} />
        <Route path="/account/email-settings" element={<MyEmailSettingsPage />} />
        <Route path="/account/email-incoming" element={<IncomingEmailPage />} />
        <Route path="/admin/style-guide" element={<StyleGuidePage />} />
        <Route path="/admin/voice-scripts" element={<VoiceScriptsPage />} />
        <Route path="/admin/voice-routes" element={<VoiceRoutesPage />} />
        <Route path="/voice/sessions/:id" element={<VoiceSessionMonitorPage />} />
        <Route path="/admin/voice-library" element={<VoiceLibraryPage />} />
        <Route path="/admin/lead-capture-forms" element={<LeadCaptureFormsPage />} />
        <Route path="/admin/landing-pages" element={<CampaignLandingPagesPage />} />
        <Route path="/admin/lead-capture-forms/:id" element={<LeadCaptureFormEditPage />} />
        <Route
          path="/admin/qualification-banks"
          element={<QualificationBanksPage />}
        />
        <Route
          path="/admin/qualification-banks/:id"
          element={<QualificationBankEditPage />}
        />
        <Route
          path="/qualification/conversations"
          element={<QualificationConversationsPage />}
        />
        <Route
          path="/qualification/conversations/:id"
          element={<QualificationConversationDetailPage />}
        />
        <Route path="/admin/dnc" element={<DncListPage />} />
        <Route path="/admin/workflow-runs" element={<WorkflowRunsPage />} />
        <Route path="/admin/billing" element={<BillingPage />} />
        <Route path="/analytics/funnel" element={<FunnelExplorerPage />} />
        <Route path="/cockpit/rep" element={<CockpitRepPage />} />
        <Route path="/cockpit/manager" element={<CockpitManagerPage />} />
        <Route path="/cockpit/board" element={<CockpitBoardPage />} />
        <Route path="/admin/voice-credentials" element={<VoiceCredentialsPage />} />
        <Route path="/admin/enrichment" element={<EnrichmentSettingsPage />} />
        <Route path="/admin/external-providers" element={<ExternalProvidersAdminPage />} />
        {/* Epic 138 — multi-line channel pool (ADR-019) */}
        <Route path="/admin/channel-lines" element={<ChannelLinesPage />} />
        {/* Epic 138.6 — routing modes, rules, queues and the escalation ladder */}
        <Route path="/admin/conversation-routing" element={<ConversationRoutingPage />} />
        <Route path="/admin/imports" element={<ImportsPage />} />
        <Route path="/admin/imports/:id" element={<ImportDetailPage />} />
        <Route path="/analytics/channels" element={<ChannelAnalyticsPage />} />
        <Route path="/analytics/attribution" element={<AttributionPage />} />
        <Route path="/analytics/mktg-sales-align" element={<MktgSalesAlignPage />} />
        <Route path="/admin/lead-qualification" element={<LeadQualificationPage />} />
        <Route path="/admin/api-builder" element={<ApiBuilderPage />} />
        <Route path="/admin/webhooks" element={<WebhooksPage />} />
        {/* Epics 200/201/202 — OAuth clients, event bus, inbound webhooks */}
        <Route path="/admin/oauth-clients" element={<OAuthClientsPage />} />
        {/* Epics 203/204 — App SDK / Placements / Marketplace */}
        <Route path="/admin/marketplace" element={<MarketplacePage />} />
        <Route path="/admin/event-subscriptions" element={<EventSubscriptionsPage />} />
        <Route path="/admin/inbound-webhooks" element={<InboundWebhooksPage />} />
        <Route path="/admin/target-accounts" element={<TargetAccountsPage />} />
        {/* Epic 73 + 74 — Flow Canvas, Marketplace, Analytics, Integrations */}
        <Route path="/admin/chains/:id/analytics" element={<WorkflowAnalyticsPage />} />
        <Route path="/admin/workflow-marketplace" element={<WorkflowMarketplacePage />} />
        <Route path="/admin/connector-integrations" element={<ConnectorIntegrationsPage />} />
        {/* Epic 75 — Appointments */}
        <Route path="/admin/appointments" element={<AppointmentsPage />} />
        {/* Epic 64 — Smart Dialer */}
        <Route path="/dialer" element={<DialerPage />} />
        <Route path="/dialer/analytics" element={<DialerAnalyticsPage />} />
        {/* Epic 65 — Coaching */}
        <Route path="/coaching/calls" element={<CoachingCallsPage />} />
        {/* Epic 63 — Send-time Analytics */}
        <Route path="/analytics/send-time" element={<SendTimeAnalyticsPage />} />
        {/* Epics 100–105 — Vertical templates and extensions */}
        <Route path="/admin/connector-marketplace" element={<ConnectorMarketplacePage />} />
        <Route path="/admin/vertical-settings" element={<VerticalSettingsPage />} />
        <Route path="/admin/tenant-hierarchy" element={<TenantHierarchyPage />} />
        <Route path="/admin/regulatory" element={<RegulatoryPage />} />
        <Route path="/coaching/conversation-intelligence" element={<ConversationIntelligencePage />} />
        <Route path="/admin/form-templates" element={<FormTemplatesPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        {/* Story 83.3 — Agency portfolio (cross-tenant view for AGENCY_OWNER). */}
        <Route path="/agency/portfolio" element={<AgencyPortfolioPage />} />
        <Route path="/cockpit" element={<CockpitBoardPage />} />
        <Route path="/admin/voice-library" element={<VoiceLibraryPage />} />
        <Route path="/admin/voice-library/clone" element={<VoiceLibraryPage />} />
        <Route path="/admin/power-dialer" element={<PowerDialerAdminPage />} />
        <Route path="/voice/sessions/:id" element={<VoiceSessionPage />} />
        <Route path="/admin/email-pools" element={<EmailPoolsPage />} />
        <Route path="/admin/email-domain-setup" element={<EmailDomainSetupPage />} />
        <Route path="/admin/sequences" element={<SequencesPage />} />
        <Route path="/admin/sequences/:id/edit" element={<SequenceEditorPage />} />
        <Route path="/admin/sequences/:id/analytics" element={<SequenceAnalyticsPage />} />
        {/* Epic 70 — Text Agent Builder + Inbox AI */}
        <Route path="/admin/text-agents" element={<TextAgentsPage />} />
        <Route path="/conversations" element={<ConversationsPage />} />
        {/* Epic 71 — Web Chat Widget */}
        <Route path="/admin/chat-widget" element={<ChatWidgetPage />} />
        {/* Epic 114 — Prospect Demos (Phase 3 + Phase 7) */}
        <Route path="/demos" element={<DemoListPage />} />
        <Route path="/demos/new" element={<DemoStudioPage />} />
        <Route path="/demos/:demoId" element={<DemoEditorPage />} />
        {/* Ticket 114.7 — Analytics funnel (SALES_MANAGER) */}
        <Route path="/demos/analytics" element={<DemoAnalyticsPage />} />
        <Route path="/admin/demos/brand-defaults" element={<DemoBrandDefaultsPage />} />
        {/* Ticket 114.8 — PLATFORM_ADMIN demo catalog + stat refresh */}
        <Route path="/admin/demos/catalog" element={<DemoCatalogAdminPage />} />
        {/* Epic — Legal & Agreements (MOU / NDA / Partnership / SOW / DPA) */}
        <Route path="/admin/sales-team" element={<SalesTeamPage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/quotes/:id" element={<QuoteEditorPage />} />
        <Route path="/agreements" element={<AgreementsListPage />} />
        <Route path="/agreements/templates" element={<AgreementTemplatesPage />} />
        <Route path="/agreements/templates/new/edit" element={<AgreementTemplateEditPage />} />
        <Route path="/agreements/templates/:id/edit" element={<AgreementTemplateEditPage />} />
        <Route path="/agreements/:id" element={<AgreementDetailPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </BrandingProvider>
  );
}
